import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import MainLayout from '@/components/layout/MainLayout';
import { SEOHead } from '@/components/seo/SEOHead';
import { buildEventListItem } from '@/utils/eventSchema';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Calendar, 
  MapPin, 
  ArrowRight, 
  Loader2, 
  Tag,
  Building2,
  Users
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * GA Event Tracking
 */
const trackEvent = (eventName, params) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, params);
  } else {
    console.log('GA Event:', eventName, params);
  }
};

/**
 * CitySectorEventsPage - SEO-optimized city + sector combined events listing
 * Route: /events/city/:citySlug/sector/:tagSlug
 */
export default function CitySectorEventsPage() {
  const { citySlug, tagSlug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${BACKEND_URL}/api/public/city/${citySlug}/sector/${tagSlug}`);
        setData(response.data);
        
        // Track page view
        trackEvent('seo_page_viewed', {
          page_type: 'city_sector',
          city: citySlug,
          tag: tagSlug,
          event_count: response.data.total
        });
        
        if (!response.data.hasEvents) {
          trackEvent('seo_empty_state_viewed', {
            page_type: 'city_sector',
            city: citySlug,
            tag: tagSlug
          });
        }
      } catch (err) {
        console.error('Error fetching events:', err);
        setError('Unable to load events.');
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, [citySlug, tagSlug]);

  // Generate SEO metadata
  const getPageMeta = () => {
    const cityName = data?.city?.name || citySlug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    const sectorName = data?.sector?.name || tagSlug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    return {
      title: `${sectorName} Events in ${cityName} | Jigsaw Conferences`,
      description: `Discover ${sectorName} trade shows, conferences and exhibitions in ${cityName}. Find upcoming events, dates and venues for ${sectorName} industry professionals.`,
      h1: `${sectorName} Events in ${cityName}`
    };
  };

  const pageMeta = getPageMeta();

  // Generate JSON-LD schema
  const generateSchema = () => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": pageMeta.title,
      "description": pageMeta.description,
      "url": `https://events.jigsawconferences.co.uk/events/city/${citySlug}/sector/${tagSlug}`,
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {"@type": "ListItem", "position": 1, "name": "Events", "item": "https://events.jigsawconferences.co.uk/events"},
          {"@type": "ListItem", "position": 2, "name": data?.city?.name || citySlug, "item": `https://events.jigsawconferences.co.uk/events/${citySlug}`},
          {"@type": "ListItem", "position": 3, "name": data?.sector?.name || tagSlug}
        ]
      }
    };
    
    if (data?.events?.length > 0) {
      schema.mainEntity = {
        "@type": "ItemList",
        "numberOfItems": data.total,
        "itemListElement": data.events.slice(0, 10).map((event, index) => buildEventListItem(event, index + 1))
      };
    }
    
    return schema;
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="min-h-screen flex justify-center items-center">
          <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
          <span className="ml-3 text-gray-600">Loading events...</span>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <SEOHead
        title={pageMeta.title}
        description={pageMeta.description}
        url={`https://events.jigsawconferences.co.uk/events/city/${citySlug}/sector/${tagSlug}`}
        schema={generateSchema()}
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-indigo-600 to-purple-600 text-white py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="text-indigo-200 text-sm mb-4">
            <Link to="/events" className="hover:text-white">Events</Link>
            <span className="mx-2">›</span>
            <Link to={`/events/${citySlug}`} className="hover:text-white">{data?.city?.name || citySlug}</Link>
            <span className="mx-2">›</span>
            <span className="text-white">{data?.sector?.name || tagSlug}</span>
          </nav>
          
          <div className="flex items-center gap-4 mb-4">
            <div className="bg-white/20 p-3 rounded-full">
              <Tag className="w-8 h-8" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold">{pageMeta.h1}</h1>
          </div>
          
          <p className="text-lg text-indigo-100 max-w-2xl">
            {data?.total || 0} upcoming {data?.sector?.name || 'sector'} events in {data?.city?.name || citySlug}.
          </p>
          
          {/* Quick stats */}
          <div className="flex flex-wrap gap-4 mt-6">
            <Badge className="bg-white/20 text-white px-4 py-2">
              <Calendar className="w-4 h-4 mr-2" />
              {data?.total || 0} Events
            </Badge>
            <Badge className="bg-white/20 text-white px-4 py-2">
              <MapPin className="w-4 h-4 mr-2" />
              {data?.city?.name}
            </Badge>
            <Badge className="bg-white/20 text-white px-4 py-2">
              <Tag className="w-4 h-4 mr-2" />
              {data?.sector?.name}
            </Badge>
          </div>
        </div>
      </section>

      {/* Venues Filter */}
      {data?.venues?.length > 0 && (
        <section className="bg-gray-50 py-4 border-b">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-gray-600 mr-2">Venues:</span>
              {data.venues.map(venue => (
                <Link key={venue.slug} to={`/events/${citySlug}/${venue.slug}`}>
                  <Badge variant="outline" className="hover:bg-indigo-50 hover:border-indigo-500 cursor-pointer">
                    <Building2 className="w-3 h-3 mr-1" />
                    {venue.name}
                  </Badge>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Events Grid */}
      <section className="py-12 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {error ? (
            <div className="text-center py-12">
              <p className="text-gray-500">{error}</p>
            </div>
          ) : !data?.hasEvents ? (
            <div className="text-center py-12">
              <Tag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-700 mb-2">
                No {data?.sector?.name} events in {data?.city?.name}
              </h2>
              <p className="text-gray-500 mb-6">Try browsing all events in this city or sector.</p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to={`/events/${citySlug}`}>
                  <Button variant="outline">
                    All {data?.city?.name} Events
                  </Button>
                </Link>
                <Link to={`/events/sector/${tagSlug}`}>
                  <Button className="bg-indigo-600 hover:bg-indigo-700">
                    All {data?.sector?.name} Events
                  </Button>
                </Link>
              </div>
            </div>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                {data.sector.name} Events in {data.city.name}
              </h2>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {data.events.map((event) => (
                  <Link
                    key={event.slug}
                    to={`/events/${event.citySlug}/${event.venueSlug}/${event.slug}`}
                    className="block group"
                  >
                    <Card className="h-full hover:shadow-lg transition-shadow border-gray-200 hover:border-indigo-500">
                      <CardContent className="p-5">
                        <h3 className="font-semibold text-gray-900 mb-3 group-hover:text-indigo-600 line-clamp-2">
                          {event.name}
                        </h3>
                        
                        <div className="space-y-2 mb-3">
                          <div className="flex items-center text-sm text-gray-600">
                            <Calendar className="w-4 h-4 mr-2 text-indigo-500 flex-shrink-0" />
                            <span>{event.dates?.display || event.dates?.start}</span>
                          </div>
                          <div className="flex items-center text-sm text-gray-600">
                            <Building2 className="w-4 h-4 mr-2 text-indigo-500 flex-shrink-0" />
                            <span className="truncate">{event.venueName}</span>
                          </div>
                          <div className="flex items-center text-sm text-gray-600">
                            <Users className="w-4 h-4 mr-2 text-indigo-500 flex-shrink-0" />
                            <span className="truncate">{event.organiserName}</span>
                          </div>
                        </div>
                        
                        <p className="text-xs text-gray-500 line-clamp-2 mb-3">
                          {event.shortDescription}
                        </p>
                        
                        <div className="flex items-center text-sm font-medium text-indigo-600 group-hover:text-indigo-700">
                          View details
                          <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Related Links */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Explore More</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold text-gray-900 mb-2">
                  More {data?.sector?.name} Events
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  View {data?.sector?.name} events across all UK cities.
                </p>
                <Link to={`/events/sector/${tagSlug}`}>
                  <Button variant="outline" className="w-full">
                    All {data?.sector?.name} Events
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold text-gray-900 mb-2">
                  All {data?.city?.name} Events
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  Browse all upcoming events in {data?.city?.name}.
                </p>
                <Link to={`/events/${citySlug}`}>
                  <Button variant="outline" className="w-full">
                    All {data?.city?.name} Events
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Subscribe CTA */}
      <section className="py-12 bg-indigo-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold mb-4">
            Never Miss a {data?.sector?.name} Event in {data?.city?.name}
          </h2>
          <p className="text-indigo-100 mb-6">
            Subscribe to our calendar feed for automatic updates.
          </p>
          <a href={`${BACKEND_URL}/api/public/calendar.ics?city=${citySlug}&tags=${data?.sector?.name}`} rel="nofollow noindex">
            <Button className="bg-white text-indigo-600 hover:bg-indigo-50">
              <Calendar className="w-4 h-4 mr-2" />
              Subscribe to Calendar
            </Button>
          </a>
        </div>
      </section>
    </MainLayout>
  );
}
